/**
 * Conversation entity: backend DTOs, API calls, the SSE stream reader, and the
 * DTO -> display mappers.
 *
 * The conversation API is agent-generic (agent "chat" powers the assistant page)
 * and streams replies as Server-Sent Events. EventSource can't POST, so
 * sendMessage() hand-parses the "data: {...}\n\n" frames off a fetch body
 * stream, handling frames split across network chunks.
 *
 * THE INVARIANT: tenant-less surface — the bearer token is the identity and the
 * server resolves the tenant. Conversations are additionally user-owned and
 * (optionally) company-anchored via companyId.
 */

import type { ChatMessage, Conversation } from '../../app/types';
import { apiDelete, apiGet } from '../apiClient';
import { getAuthToken } from '../auth';
import { formatDate } from './format';

const API_BASE_URL = import.meta.env.VITE_API_BASE_URL?.replace(/\/$/, '') ?? '';

/* ------------------------------------------------------------------- DTOs */

/**
 * An agent output block. Blocks are polymorphic on `type`; the assistant chat
 * only renders text/question/warning specially and stringifies the rest.
 * Stored-message blocks arrive as plain camelCase dictionaries with the same
 * keys, so one shape serves both the SSE stream and GET /api/conversations/{id}.
 */
export interface OutputBlockDto {
  type: string;
  id?: string;
  /** text block */
  content?: string;
  format?: string;
  /** question block */
  question?: string;
  options?: string[];
  reason?: string;
  /** warning block */
  title?: string;
  message?: string;
  severity?: string;
  [key: string]: unknown;
}

export interface ConversationMessageDto {
  role: string;
  /** Null for assistant turns whose payload is entirely in `blocks`. */
  content?: string | null;
  blocks?: OutputBlockDto[] | null;
  timestamp: string;
}

export interface ConversationSummaryDto {
  id: string;
  agentId: string;
  title: string;
  isComplete: boolean;
  lastMessageAt: string;
  messageCount: number;
  companyId?: string | null;
  expiresAt?: string | null;
}

export interface ConversationDto {
  id: string;
  agentId: string;
  title: string;
  isComplete: boolean;
  lastMessageAt: string;
  messages: ConversationMessageDto[];
  companyId?: string | null;
  expiresAt?: string | null;
}

/** Body for POST /api/conversations (ConversationRequest on the backend). */
export interface SendMessageRequest {
  agentId: string;
  /** Required unless `continuation` — the backend rejects empty otherwise. */
  message?: string;
  /** Continue an existing conversation; omit to create a new one. */
  conversationId?: string;
  /** Company to anchor a NEW conversation to (null = general conversation). */
  companyId?: string | null;
  context?: Record<string, unknown>;
  /** Re-run the agent over existing history without a new message. */
  continuation?: boolean;
}

/* ------------------------------------------------------------ SSE frames */

interface StatusFrame {
  type: 'status';
  message: string;
  phase?: string;
}

interface BlockFrame {
  type: 'block';
  block: OutputBlockDto;
  isComplete?: boolean;
}

interface CompleteFrame {
  type: 'complete';
  conversationId?: string | null;
  isConversationComplete?: boolean;
}

interface ErrorFrame {
  type: 'error';
  error: string;
  code?: string;
}

interface UsageFrame {
  type: 'usage';
  promptTokens?: number;
  completionTokens?: number;
}

type StreamFrame = StatusFrame | BlockFrame | CompleteFrame | ErrorFrame | UsageFrame;

/** Callbacks invoked as SSE frames arrive. Usage frames are ignored for now. */
export interface StreamHandlers {
  onStatus?: (message: string, phase?: string) => void;
  /** isComplete=false means the same block (by id) may be re-sent updated. */
  onBlock?: (block: OutputBlockDto, isComplete: boolean) => void;
  onComplete?: (conversationId: string | null, isConversationComplete: boolean) => void;
  onError?: (error: string, code?: string) => void;
}

/* ------------------------------------------------------------- API calls */

/** GET /api/conversations — summaries (no messages), newest first not guaranteed. */
export function listConversations(): Promise<ConversationSummaryDto[]> {
  return apiGet<ConversationSummaryDto[]>('/api/conversations');
}

/** GET /api/conversations/{id} — full conversation with display messages. */
export function getConversation(id: string): Promise<ConversationDto> {
  return apiGet<ConversationDto>(`/api/conversations/${id}`);
}

/** DELETE /api/conversations/{id} — soft delete; vanishes from reads immediately. */
export function deleteConversation(id: string): Promise<void> {
  return apiDelete<void>(`/api/conversations/${id}`);
}

/**
 * POST /api/conversations and stream the reply.
 *
 * Resolves when the stream ends (after complete/error, or on disconnect).
 * Network/HTTP failures are routed to handlers.onError rather than thrown, so
 * callers have exactly one error path to render in-thread.
 */
export async function sendMessage(
  request: SendMessageRequest,
  handlers: StreamHandlers,
  signal?: AbortSignal,
): Promise<void> {
  let response: Response;
  try {
    const token = await getAuthToken();
    response = await fetch(`${API_BASE_URL}/api/conversations`, {
      method: 'POST',
      headers: {
        Accept: 'text/event-stream',
        'Content-Type': 'application/json',
        ...(token ? { Authorization: `Bearer ${token}` } : {}),
      },
      body: JSON.stringify(request),
      signal,
    });
  } catch (err) {
    if (signal?.aborted) return;
    handlers.onError?.(err instanceof Error ? err.message : 'Could not reach the assistant.');
    return;
  }

  // Non-2xx means the request never reached the SSE writer (auth/middleware);
  // the body is the backend's JSON error envelope, not an event stream.
  if (!response.ok || !response.body) {
    let message = response.statusText || `Request failed (${response.status})`;
    try {
      const envelope = (await response.json()) as { error?: string } | null;
      if (envelope && typeof envelope.error === 'string' && envelope.error) {
        message = envelope.error;
      }
    } catch {
      // Not JSON — keep the status-text fallback.
    }
    handlers.onError?.(message, `HTTP_${response.status}`);
    return;
  }

  const reader = response.body.getReader();
  const decoder = new TextDecoder();
  let buffer = '';

  const dispatch = (rawEvent: string) => {
    // Per the SSE spec an event's data is its "data:" lines joined with \n
    // (the backend writes single-line frames, but stay spec-correct).
    const data = rawEvent
      .split('\n')
      .filter((line) => line.startsWith('data:'))
      .map((line) => line.slice(5).trimStart())
      .join('\n');
    if (!data) return;

    let frame: StreamFrame;
    try {
      frame = JSON.parse(data) as StreamFrame;
    } catch {
      return; // Malformed frame — skip rather than kill the stream.
    }

    switch (frame.type) {
      case 'status':
        handlers.onStatus?.(frame.message, frame.phase);
        break;
      case 'block':
        handlers.onBlock?.(frame.block, frame.isComplete ?? true);
        break;
      case 'complete':
        handlers.onComplete?.(frame.conversationId ?? null, frame.isConversationComplete ?? false);
        break;
      case 'error':
        handlers.onError?.(frame.error, frame.code);
        break;
      default:
        break; // usage (and future frame types): no UI yet.
    }
  };

  try {
    for (;;) {
      const { done, value } = await reader.read();
      if (done) break;
      buffer += decoder.decode(value, { stream: true });
      // Frames are terminated by a blank line; anything after the last "\n\n"
      // is a partial frame and stays buffered for the next chunk.
      let boundary = buffer.indexOf('\n\n');
      while (boundary !== -1) {
        const rawEvent = buffer.slice(0, boundary);
        buffer = buffer.slice(boundary + 2);
        dispatch(rawEvent);
        boundary = buffer.indexOf('\n\n');
      }
    }
    // Flush a final unterminated frame, if the server closed mid-write.
    buffer += decoder.decode();
    if (buffer.trim()) dispatch(buffer);
  } catch (err) {
    if (signal?.aborted) return;
    handlers.onError?.(err instanceof Error ? err.message : 'The connection was interrupted.');
  }
}

/* ---------------------------------------------------------------- Mappers */

/** "Expires in N days" from an ISO expiry timestamp; '' when it never expires. */
export function expiresInLabel(expiresAt: string | null | undefined): string {
  if (!expiresAt) return '';
  const expires = new Date(expiresAt).getTime();
  if (Number.isNaN(expires)) return '';
  const days = Math.ceil((expires - Date.now()) / 86_400_000);
  if (days <= 0) return 'Expires today';
  if (days === 1) return 'Expires in 1 day';
  return `Expires in ${days} days`;
}

/**
 * Flatten an output block to display text. Question blocks carry their options
 * as bullet lines (ChatMessage is text-only); unknown block types are skipped.
 */
export function blockToText(block: OutputBlockDto): string {
  switch (block.type) {
    case 'text':
      return block.content ?? '';
    case 'question': {
      const parts: string[] = [];
      if (block.question) parts.push(block.question);
      if (block.options && block.options.length > 0) {
        parts.push(block.options.map((o) => `• ${o}`).join('\n'));
      }
      return parts.join('\n\n');
    }
    case 'warning': {
      const parts = [block.title, block.message].filter(
        (p): p is string => typeof p === 'string' && p.length > 0,
      );
      return parts.join('\n');
    }
    default:
      return '';
  }
}

/** ConversationMessageDto -> display ChatMessage; null when nothing showable. */
function mapMessage(dto: ConversationMessageDto, id: string): ChatMessage | null {
  const role = dto.role === 'user' ? 'user' : dto.role === 'assistant' ? 'assistant' : null;
  if (!role) return null;

  const blockText = (dto.blocks ?? [])
    .map(blockToText)
    .filter((t) => t.length > 0)
    .join('\n\n');
  const text = [dto.content ?? '', blockText].filter((t) => t.trim().length > 0).join('\n\n');
  if (!text) return null;

  return { id, role, text };
}

/** ConversationSummaryDto -> display Conversation (messages load on open). */
export function mapConversationSummary(dto: ConversationSummaryDto): Conversation {
  return {
    id: dto.id,
    title: dto.title || 'New conversation',
    startedAt: formatDate(dto.lastMessageAt),
    expiresIn: expiresInLabel(dto.expiresAt),
    messages: [],
  };
}

/** ConversationDto -> display Conversation with mapped messages. */
export function mapConversation(dto: ConversationDto): Conversation {
  const messages = (dto.messages ?? [])
    .map((m, i) => mapMessage(m, `${dto.id}-m${i}`))
    .filter((m): m is ChatMessage => m !== null);
  return {
    id: dto.id,
    title: dto.title || 'New conversation',
    startedAt: formatDate(dto.lastMessageAt),
    expiresIn: expiresInLabel(dto.expiresAt),
    messages,
  };
}
